import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Grid,
  styled,
  Typography,
  useTheme,
  useMediaQuery,
  Skeleton,
} from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";

const MainContainer = styled(Box)(({ theme }) => ({
  background: "#000",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "60px 24px",
  position: "relative",
  overflow: "hidden",

  "&::after": {
    content: '""',
    position: "absolute",
    width: "95%",
    height: "80%",
    left: "65%",
    top: "10%",
    bottom: "10%",
    transform: "translateX(-100%)",
    background:
      "radial-gradient(circle, rgba(99, 102, 241, 0.65) 0%, rgba(99, 102, 241, 0) 70%)",
    filter: "blur(100px)",
    zIndex: 0,
  },
}));

const ContentWrapper = styled(Box)(({ theme }) => ({
  width: "100%",
  maxWidth: "1200px",
  margin: "0 auto",
  position: "relative",
  zIndex: 1,
}));

const HeaderText = styled(Typography)(({ theme }) => ({
  background: "linear-gradient(262.41deg, #1DA1F2 -8.32%, #6843EC 93.3%)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  textAlign: "center",
  fontWeight: "bold",
  marginBottom: theme.spacing(8),
  fontSize: "32px",
  [theme.breakpoints.down("sm")]: {
    fontSize: "28px",
  },
}));

const ThoughtLeadershipCard = styled(Card)(({ theme }) => ({
  position: "relative",
  borderRadius: theme.spacing(2),
  overflow: "hidden",
  height: "300px",
  cursor: "pointer",
  [theme.breakpoints.down("md")]: {
    height: "250px",
  },
  [theme.breakpoints.down("sm")]: {
    height: "220px",
  },
}));

const DashboardCard = styled(Card)(({ theme }) => ({
  position: "relative",
  borderRadius: theme.spacing(2),
  overflow: "hidden",
  backgroundColor: "#5D70FF",
  color: "white",
  height: "300px",
  cursor: "pointer",
  [theme.breakpoints.down("md")]: {
    height: "250px",
  },
  [theme.breakpoints.down("sm")]: {
    height: "150px",
  },
}));

const CardOverlay = styled(Box)(({ theme }) => ({
  position: "absolute",
  bottom: 0,
  left: 0,
  padding: theme.spacing(2),
  width: "100%",
  background: "linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0) 100%)",
}));

const MainTitle = styled(Typography)(({ theme }) => ({
  fontWeight: "bold",
  marginBottom: theme.spacing(1),
  fontSize: "18px",
  [theme.breakpoints.down("sm")]: {
    fontSize: "16px",
  },
}));

const API_URL = "https://dev.ienergydigital.com/task-api/api/cms/entries/";

export default function BreakingBoundaries() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const getNewSlideList = async (page = 1, limit = 100) => {
    try {
      setLoading(true);
      const { data } = await axios.get(API_URL, {
        params: { type: "bottom_section", page, limit },
      });
      const list = data?.data?.entries || [];
      setEntries(list);
    } catch (err) {
      console.error(err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getNewSlideList();
  }, []);

  // Split API entries by contentJson.slotType so we can place them
  // into the "large" (thought leadership) and "small/dashboard" slots.
  const thoughtLeadership = entries
    .filter((e) => e?.contentJson?.slotType === "large")
    .map((e) => e.contentJson);

  const dashboards = entries
    .filter((e) => e?.contentJson?.slotType !== "large")
    .map((e) => e.contentJson);

  const tl0 = thoughtLeadership[0];
  const tl1 = thoughtLeadership[1];
  const db0 = dashboards[0];
  const db1 = dashboards[1];
  const db2 = dashboards[2];
  const db3 = dashboards[3];

  const openLink = (url) => {
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  const renderThoughtCard = (item) => (
    <ThoughtLeadershipCard onClick={() => openLink(item?.linkUrl)}>
      {loading ? (
        <Skeleton variant="rectangular" width="100%" height="100%" />
      ) : (
        <>
          <CardMedia
            component="img"
            height="100%"
            image={item?.image}
            alt={item?.heading || "Thought Leadership"}
            sx={{ filter: "brightness(0.5)", objectFit: "cover" }}
          />
          <CardOverlay>
            <MainTitle variant="h5" sx={{ color: "white" }}>
              {item?.heading || "Thought Leadership Insights"}
            </MainTitle>
            <Typography
              variant="body2"
              sx={{ color: "#8C8CC2", fontSize: "12px" }}
            >
              {item?.desc}
            </Typography>
          </CardOverlay>
        </>
      )}
    </ThoughtLeadershipCard>
  );

  const renderDashboardCard = (item, imageFirst) => (
    <DashboardCard onClick={() => openLink(item?.linkUrl)}>
      {loading ? (
        <Skeleton variant="rectangular" width="100%" height="100%" />
      ) : isMobile ? (
        <Box sx={{ display: "flex", height: "100%" }}>
          <CardMedia
            component="img"
            sx={{ width: "40%", objectFit: "cover" }}
            image={item?.image}
            alt={item?.heading || "Dashboard"}
          />
          <CardContent sx={{ width: "60%", p: 2 }}>
            <Typography variant="body2" sx={{ fontSize: "15px" }}>
              {item?.desc}
            </Typography>
          </CardContent>
        </Box>
      ) : imageFirst ? (
        <>
          <CardMedia
            component="img"
            sx={{ height: "55%", objectFit: "cover" }}
            image={item?.image}
            alt={item?.heading || "Dashboard"}
          />
          <CardContent sx={{ height: "45%", p: 2 }}>
            <Typography variant="body2" sx={{ fontSize: "15px" }}>
              {item?.desc}
            </Typography>
          </CardContent>
        </>
      ) : (
        <>
          <CardContent sx={{ height: "45%", p: 2 }}>
            <Typography variant="body2" sx={{ fontSize: "15px" }}>
              {item?.desc}
            </Typography>
          </CardContent>
          <CardMedia
            component="img"
            sx={{ height: "55%", objectFit: "cover" }}
            image={item?.image}
            alt={item?.heading || "Dashboard"}
          />
        </>
      )}
    </DashboardCard>
  );

  if (error) {
    return (
      <MainContainer>
        <ContentWrapper>
          <Typography sx={{ color: "white", textAlign: "center" }}>
            Failed to load content.
          </Typography>
        </ContentWrapper>
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      <ContentWrapper>
        <HeaderText>
          Breaking Boundaries with
          <br />
          AI-Driven Solutions
        </HeaderText>

        <Grid container spacing={2}>
          {/* Left column */}
          <Grid item xs={12} md={6}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                {renderThoughtCard(tl0)}
              </Grid>

              <Grid container item xs={12} spacing={2}>
                <Grid item xs={12} sm={6} md={6}>
                  {renderDashboardCard(db0, false)}
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  {renderDashboardCard(db1, true)}
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {/* Right column */}
          <Grid item xs={12} md={6}>
            <Grid container spacing={2}>
              <Grid container item xs={12} spacing={2}>
                <Grid item xs={12} sm={6} md={6}>
                  {renderDashboardCard(db2, false)}
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  {renderDashboardCard(db3, true)}
                </Grid>
              </Grid>

              <Grid item xs={12}>
                {renderThoughtCard(tl1)}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </ContentWrapper>
    </MainContainer>
  );
}